import React, { useEffect, useState , useLayoutEffect} from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import ParentConsoleNav from "@/components/ParentConsoleNav";
import ActiveLearnerBar from "@/components/ActiveLearnerBar";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";

// Cycle 60 · Phase D · Which route paths mount the parent console nav.
const PARENT_CONSOLE_PATHS = [
  "/parents",
  "/safety",
  "/achievements",
  "/family",
];
function isParentConsolePath(pathname) {
  if (!pathname) return false;
  return PARENT_CONSOLE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function ParentConsoleNavSlot() {
  const { pathname } = useLocation();
  const mode = useAppMode();
  if (!isParentConsolePath(pathname) || mode === "grownup") return null;
  return (
    <div className="w-full max-w-5xl mx-auto px-3 pt-3 md:px-4 md:pt-4">
      <ActiveLearnerBar role="parent" />
      <ParentConsoleNav />
    </div>
  );
}

import { Star, Menu, X, ArrowLeft, Home as HomeIcon, Trophy, Flame,
         Sprout, ShieldCheck, FileText, Hand, Users, Newspaper,
         GraduationCap, Mail, Share2, BookOpen, HelpCircle, Compass,
         HeartHandshake, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { loadProgress, updateSettings } from "@/lib/progress";
import { levelForXp, worldCompletion } from "@/lib/progression";
import { pillarForPath, PILLARS } from "@/lib/pillars";
import { useAgeGroup, useHasStreaks, useHasQuizzes, useTier } from "@/lib/ageGroup";
import { getBirthCountryCode } from "@/lib/kidProfile";
import { COUNTRIES, flagUrl } from "@/data/countries";
import usePageDwell from "@/lib/usePageDwell";
import ShareButton from "@/components/ShareButton";
import LanguagePrompt from "@/components/LanguagePrompt";
import OfflineIndicator from "@/components/OfflineIndicator";
import ContinentCertWatcher from "@/components/ContinentCertWatcher";
import { keyObjectivesForAge } from "@/lib/keyObjectives";
import { installTapToHear } from "@/lib/tapToHear";
import { cn } from "@/lib/utils";
import GrownupPinGate, { isGrownupGatePassed } from "@/components/GrownupPinGate";
import SoundToggle from "@/components/SoundToggle";
import ChildSwitcher from "@/components/ChildSwitcher";
import ExplorerRankBadge from "@/components/ExplorerRankBadge";
import ProgressPill from "@/components/ProgressPill";
import { FeatureSearch } from "@/components/FeatureDiscovery";
import { IndexabilityMeta } from "@/lib/indexability";
import FooterColumns from "@/components/FooterColumns";
import { isActivityRoute, isActiveLearningRoute } from "@/lib/siteNav";
import ActivityTopBar from "@/components/ActivityTopBar";
import TravelTopBar from "@/components/TravelTopBar";
import { useTravelMode } from "@/lib/travelMode";
import { isAirlinePath, airlineSessionActive } from "@/lib/presentation";
import InstallPrompt from "@/components/InstallPrompt";
// Cycle 56 · Fading-audio auto-adjust on session start.
import { hydrateFromChild, maybeAutoAdjust } from "@/lib/readingLevel";
import { getStreak, getWeekly, setWeeklyGoal } from "@/lib/masteryLadder";
import { companionForStreak } from "@/data/companionStages";
import { getCompanion } from "@/lib/companion";
// Cycle 13.5 · nav registry extracted to /lib/nav.js so the audit
// script and any future menu-consumer can import it dependency-free.
import { NAV_PATHS, NAV_EMOJI, PRIMARY_KEYS, PILLAR_TAB_EMOJI } from "@/lib/nav";
// Cycle 13.6 · desktop dropdown + mobile drawer extracted to their own
// files so this Layout stays focused on chrome / header / tabbar and
// each menu surface can be iterated on independently.
import DesktopDropdown from "@/components/layout/DesktopDropdown";
import MobileDrawer from "@/components/layout/MobileDrawer";
import { track } from "@/lib/track";
// Canonical hub keys for data-testids / analytics (spec: today · learn · play · explore · myquest)
const HUB_TESTID = { home: "today", "learn-pathways": "learn", "play-create": "play", globe: "explore", "my-quest": "myquest" };
// Cycle 18 · one-time first-night delight
import StudySkyDelight from "@/components/StudySkyDelight";
import GamesQuickLinkFab from "@/components/GamesQuickLinkFab";
import UniversalSearch from "@/components/UniversalSearch";
import AccessibilityPanel from "@/components/AccessibilityPanel";
import NextAdventure from "@/components/NextAdventure";
import { rememberVisit } from "@/lib/recentAdventures";
import { showsNextAdventure } from "@/lib/nextAdventure";
import { useAppMode, getMode, setMode, GROWNUP_NAV } from "@/lib/appMode";
import { roleById, roleForPath } from "@/lib/roleRegistry";
import { useAttentionSettings } from "@/lib/attentionSettings";

const PILLAR_TABS = PILLARS.map((p) => ({
  key: p.key,
  to: p.to,
  label: p.label,
  // Feb 2026 (Cycle 13.4) — shorter label reserved for the mobile
  // bottom nav. "My Journey" wraps to two lines at 390px; "Journey"
  // fits on one and still reads clearly.
  shortLabel: p.key === "journey" ? "Journey" : p.label,
  emoji: PILLAR_TAB_EMOJI[p.key] || "•",
  bg: p.bg,
}));
// "More" menu — surfaces every feature so kids and parents can always reach
// any page from any screen. Primary tabs above stay focused on kid-favourites;
// everything else (including the 4 home hubs) lives here in a single tidy
// dropdown / mobile drawer. "knowledge", "geo-lines" and
// "advanced" are intentionally omitted from the primary/secondary menus
// (Feb 2026): Knowledge Quest was merged into Treasure Quest, and the
// old Advanced Challenges geography quiz now lives inside Treasure Quest
// at the Pioneer age tier where questions get genuinely harder.
// "languages" is now surfaced inside the Library nav group below.
const SECONDARY_KEYS = [
  "hub-learn", "hub-create", "hub-play", "hub-discover",
  "countries", "learn-topics",
  "puzzles", "math", "maths-academy",
  "academy", "live-quest", "everything",
  "quiz", "campaigns", "stickers", "passport",
  "dream-trip", "learning-plan", "schedule", "term-report", "parents", "teacher",
];

// 7 kid-friendly sections — approved by the user (Feb 2026).
//
//   1. Home            — quick-tap home + today's lesson (already in top row)
//   2. Subjects        — all 12 subjects (collapsed accordion by default)
//   3. Games           — every game & quiz the app offers
//   4. Passport        — the child's identity + trophies + streak
//   5. Progress        — learning plan · statistics · term report
//   6. Library         — Explore / Discover surfaces + creation studios
//   7. For Grown-ups 🔒 — parent / teacher / admin (PIN-gated)
//
// The 2-click rule: every feature must be reachable in ≤ 2 taps from
// any screen. Section headers act as tap #1; feature tiles below act
// as tap #2. Nothing else should ever be more than 2 taps away.
const NAV_GROUPS = [
  // Jun 2026 · "More" menu re-organised into simple groups (Explore ·
  // Learn · Subjects · Play · Create · Academies · Special Quests · My
  // Journey · For Grown-ups). Every previous key is preserved — nothing
  // removed, renamed or moved out of More. `hero` renders one prominent
  // full-width tile with a short description (Globe).
  { id: "explore", emoji: "🌍", titleKey: "ui.navGroups.explore", titleEn: "Explore",
    desc: "Countries, continents and the world", tint: "bg-sky-100 hover:bg-sky-200",
    hero: "globe", heroLabel: "Globe", heroDesc: "Explore countries, continents and the world.",
    keys: ["globe", "countries", "continents", "stories", "animals", "geo-lines", "dream-trip", "campaigns", "play-home"] },
  { id: "learn", emoji: "📚", titleKey: "ui.navGroups.learn", titleEn: "Learn",
    desc: "Reading, writing, life skills and more", tint: "bg-emerald-100 hover:bg-emerald-200",
    keys: ["reading", "leveled-reading", "handwriting", "money-time", "life-skills", "typing", "sign-language", "languages", "ask"] },
  // Subjects keeps its accordion (collapsed for younger tiers; "All 12 subjects" toggle).
  { id: "subjects", emoji: "🗂️", titleKey: "ui.navGroups.subjects", titleEn: "Subjects",
    desc: "All 12 subjects, topic by topic", tint: "bg-emerald-50 hover:bg-emerald-100",
    keys: ["topic-geography", "topic-maths", "topic-science", "topic-english",
           "topic-history", "topic-languages", "topic-art", "topic-music",
           "topic-ict", "topic-pe", "topic-re", "topic-pshe"] },
  { id: "games", emoji: "🎮", titleKey: "ui.navGroups.games", titleEn: "Play",
    desc: "Games, quizzes, puzzles and challenges", tint: "bg-rose-100 hover:bg-rose-200",
    preview: 6,
    keys: ["arcade",
           "duel-quiz", "duel-tables", "duel-spell", "duel-continent", "duel-reaction",
           "quiz", "spelling-bee", "math", "code-breaker",
           "puzzles", "name-place-animal", "tic-tac-toe",
           "key-quest", "sign-match", "sign-sprint",
           "games-hub"] },
  { id: "art", emoji: "🎨", titleKey: "ui.navGroups.art", titleEn: "Create",
    desc: "Colour, draw and make", tint: "bg-fuchsia-100 hover:bg-fuchsia-200",
    keys: ["coloring", "pixel-art", "mandala", "kaleidoscope", "stained-glass",
           "flag-artist", "draw-along", "printables"] },
  { id: "academies", emoji: "🎓", titleKey: "ui.navGroups.academies", titleEn: "Academies",
    desc: "Structured courses with mastery and certificates", tint: "bg-orange-100 hover:bg-orange-200",
    keys: ["academy", "maths-academy", "treasure-quest"] },
  { id: "quests", emoji: "✨", titleKey: "ui.navGroups.quests", titleEn: "Special Quests",
    desc: "Times tables, English, languages, AI, code and live play", tint: "bg-yellow-100 hover:bg-yellow-200",
    keys: ["times-tables-quest", "english-quest", "language-quest", "code-quest", "learn-ai", "live-quest"] },
  { id: "journey", emoji: "📍", titleKey: "ui.navGroups.journey", titleEn: "My Journey",
    desc: "Your schedule and stickers", tint: "bg-amber-100 hover:bg-amber-200",
    keys: ["schedule", "stickers"] },
  // 🔒 For Grown-ups — unchanged (PIN-gated where required).
  { id: "grownup", emoji: "🔒", titleKey: "ui.navGroups.grownup", titleEn: "For Grown-ups",
    desc: "Parents, teachers, schools and planning", tint: "bg-slate-100 hover:bg-slate-200",
    gated: true,
    keys: ["everything", "parents", "teacher", "teacher-console", "homeschool-planner", "tutor-console", "schools", "curriculum", "learning-plan", "term-report",
           "my-quests", "tools-hub", "admin-hub"] },
];

// Routes where the global Back+Home strip is hidden. Home, the 5 pillar
// hubs (already in the bottom tab bar) and every `/hub/*` alias.
const TOP_LEVEL_PATHS = new Set([
  "/", "/explore", "/play", "/create", "/adventure", "/my-world",
  // Feb 2026 nav audit — landing pages that already render their own
  // sticky back-nav strip. Adding them here prevents the global
  // GlobalBackHome from duplicating the affordance.
  "/about", "/schools",
]);
function isTopLevel(path) {
  if (TOP_LEVEL_PATHS.has(path)) return true;
  if (path.startsWith("/hub/")) return true;
  // HelpHub has its own back chip in its hero.
  if (path === "/help" || path.startsWith("/help/")) return true;
  // Feb 2026 · Routes hosted inside <ActivityShell /> already render a
  // sticky Back chip in their unified activity header. Suppress the
  // GlobalBackHome strip on these paths so kids never see two Back
  // buttons stacked.
  if (path === "/first-quest") return true;
  if (path === "/quiz" || path.startsWith("/quiz/")) return true;
  if (path.startsWith("/practice/")) return true;
  // Manipulative tools each render their own "← Teach & Explain" chip
  // inside the ManipulativeFrame header. GlobalBackHome would be a
  // second Back on the same page.
  if (path.startsWith("/tools/")) return true;
  // Feb 2026 · CRO refresh — /for-schools and /impact are public B2B/
  // trust landing pages with their own local Home chip in the hero.
  if (path === "/for-schools") return true;
  if (path === "/impact") return true;
  // Schools admin dashboard has its own local Back chip in the hero.
  if (path.startsWith("/schools/")) return true;
  return false;
}

// Compressed footer link — kept as a stand-alone helper so the Layout
// footer stays readable. Siblings are visually separated by CSS gap.
function FooterLink({ to, testid, children }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className="inline-flex items-center hover:text-emerald-900 underline underline-offset-2 min-h-[44px] lg:min-h-6 px-1 leading-tight"
    >
      {children}
    </NavLink>
  );
}


// Global "← Back" + "🏠 Home" pair rendered just below the header on
// every non-top-level page. Chosen carefully so:
//   • Tap targets are ≥44 px so a 4-year-old thumb never misses.
//   • Icons + short word labels so pre-readers see the arrow/house glyph
//     even before they can read the text.
//   • Sticky under the main header so the pair stays reachable while a
//     child scrolls a long lesson or country page.
function GlobalBackHome() {
  const location = useLocation();
  const navigate = useNavigate();
  if (isTopLevel(location.pathname)) return null;

  /**
   * Strictly "previous page or action, nothing else":
   *   • If the SPA has history (user tapped through in-app links), use
   *     navigate(-1) — same as the browser Back button. This preserves
   *     scroll position, restores subject picker state, etc.
   *   • Only when the child arrived via a fresh deep-link (nothing in
   *     history) do we fall back to Home — otherwise nav(-1) would
   *     leave them stuck on the same URL.
   * We detect deep-link entry with window.history.state?.idx: react-
   *   router-dom v6 attaches an incrementing idx to each navigation
   *   entry; a value of 0 means "first entry in the SPA session".
   */
  const goBack = () => {
    try {
      const idx = window.history.state && typeof window.history.state.idx === "number"
        ? window.history.state.idx
        : null;
      if (idx === null || idx > 0) {
        navigate(-1);
        return;
      }
    } catch { /* fall through */ }
    // Deep-link entry — no history to go back to. Only NOW fall to /.
    navigate("/");
  };
  return (
    <div
      className="sticky top-14 md:top-20 z-30 bg-paper-50/95 dark:bg-night-900/95 backdrop-blur border-b border-paper-200 dark:border-night-700"
      data-testid="global-backhome-strip"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          data-testid="global-back-btn"
          aria-label="Go back"
          className="inline-flex items-center gap-1.5 h-10 min-w-[44px] px-3 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 active:translate-y-0 transition-all"
        >
          <ArrowLeft size={16} strokeWidth={3} />
          <span>Back</span>
        </button>
        <NavLink
          to="/"
          end
          data-testid="global-home-btn"
          aria-label="Go to home"
          className={({ isActive }) => cn(
            "inline-flex items-center gap-1.5 h-10 min-w-[44px] px-3 rounded-full border-[3px] border-slate-900 font-bold text-sm shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 active:translate-y-0 transition-all",
            isActive ? "bg-slate-900 text-white" : "bg-yellow-300 text-slate-900 hover:bg-yellow-400",
          )}
        >
          <HomeIcon size={16} strokeWidth={3} />
          <span>Home</span>
        </NavLink>
      </div>
    </div>
  );
}

// ─── StreakStrip ─────────────────────────────────────────────────────
// Compact, single-row Foxy + streak + weekly ring strip. Global (Feb
// 2026) — rendered once in Layout so every page sees the same widget.
// No flashcards CTA here: kids reach the deck from Games. Prevents
// competing with Play Now on the classic Home.
function StreakStrip() {
  const [streak, setStreak] = useState(getStreak());
  const [weekly, setWeekly] = useState(getWeekly());
  const [companion, setCompanion] = useState(getCompanion());
  useEffect(() => {
    const refresh = () => { setStreak(getStreak()); setWeekly(getWeekly()); setCompanion(getCompanion()); };
    window.addEventListener("mwq-ladder-changed", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("we-companion-changed", refresh);
    window.addEventListener("we-active-child-changed", refresh);
    return () => {
      window.removeEventListener("mwq-ladder-changed", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("we-companion-changed", refresh);
      window.removeEventListener("we-active-child-changed", refresh);
    };
  }, []);
  const target = weekly.target || 3;
  const done = weekly.completedCount || 0;
  const pct = Math.min(100, Math.round((done / target) * 100));
  const r = 18, stroke = 5;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  // Cycle 56 · Show the CHOSEN companion (Compass/Globy/Foxy/...) in
  // the strip. The `companionForStreak` overlay is retained so
  // streak-milestone accessories still layer on top.
  const stageOverlay = companionForStreak(streak.current)?.overlay;
  return (
    <div className="my-2 rounded-full border-[2.5px] border-slate-900 bg-white/95 px-2.5 py-1.5 shadow-[0_3px_0_0_rgba(15,23,42,1)] flex items-center gap-2.5 overflow-x-auto" data-testid="global-streak-strip">
      <div className={`relative flex-shrink-0 h-9 w-9 rounded-full border-[2px] border-slate-900 ${companion.color} flex items-center justify-center`} title={`${companion.name} · your companion`} data-testid="global-streak-companion">
        <span className="text-xl leading-none" aria-label={companion.name}>{companion.emoji}</span>
        {stageOverlay && <span className="absolute -top-1 -right-1 text-sm leading-none" aria-hidden>{stageOverlay}</span>}
      </div>
      <div className="inline-flex items-center gap-1 flex-shrink-0" data-testid="global-streak">
        <Flame size={14} strokeWidth={2.5} className="text-orange-600" />
        <span className="font-heading text-sm font-bold whitespace-nowrap">{streak.current}d</span>
      </div>
      <span aria-hidden className="hidden sm:inline-block h-5 w-px bg-slate-200" />
      <div className="flex items-center gap-1.5 flex-shrink-0" data-testid="global-weekly">
        <svg width="38" height="38" viewBox="0 0 38 38" aria-hidden>
          <circle cx="19" cy="19" r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
          <circle cx="19" cy="19" r={r} fill="none" stroke="#10B981" strokeWidth={stroke}
            strokeDasharray={`${dash} ${c}`} strokeLinecap="round" transform="rotate(-90 19 19)" />
          <text x="19" y="23" textAnchor="middle" fontSize="9" fill="#0F172A" className="font-heading font-bold">{done}/{target}</text>
        </svg>
        <div className="hidden md:inline-flex items-center gap-1" data-testid="global-goal-buttons">
          {[2, 3, 5].map((n) => (
            <button key={n} type="button" onClick={() => { setWeeklyGoal(n); setWeekly(getWeekly()); }}
              data-testid={`global-goal-${n}`}
              className={`h-5 w-5 inline-flex items-center justify-center rounded-full border-[1.5px] border-slate-900 font-bold text-[9px] transition-all ${target === n ? "bg-emerald-300" : "bg-white hover:bg-yellow-50"}`}>
              {n}
            </button>
          ))}
        </div>
      </div>
      <span className="text-[10px] font-bold text-slate-500 truncate hidden md:inline-block ml-1">
        {companion.name}{streak.current > 0 ? ` · ${streak.current}d streak` : " · your companion"}
      </span>
    </div>
  );
}

export default function Layout() {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(loadProgress());
  const [mobileMore, setMobileMore] = useState(false);
  const [brandSpin, setBrandSpin] = useState(false);

  // Feb 2026 · Cycle 58 (Part 4) — Night mode temporarily disabled.
  // Force-strip the `dark` class on every mount / storage event so
  // any old localStorage flag never flips the theme.
  useEffect(() => {
    const apply = () => {
      try { document.documentElement.classList.remove("dark"); }
      catch { /* SSR */ }
    };
    apply();
    window.addEventListener("storage", apply);
    return () => window.removeEventListener("storage", apply);
  }, []);
  // Cycle 56 · On boot + on active-child switch, hydrate the reading
  // level from the child record then run the (silent) auto-adjust.
  // No UI notification on demotion (per spec); parents see the live
  // level via /family/learner. Runs at most once per session/child.
  useEffect(() => {
    const run = () => {
      try {
        hydrateFromChild();
        maybeAutoAdjust();
      } catch { /* ignore */ }
    };
    run();
    window.addEventListener("we-active-child-changed", run);
    window.addEventListener("we-children-changed", run);
    return () => {
      window.removeEventListener("we-active-child-changed", run);
      window.removeEventListener("we-children-changed", run);
    };
  }, []);

  const [, , ageMeta] = useAgeGroup();
  const tier = useTier();
  const hasQuizzes = useHasQuizzes();
  const hasStreaks = useHasStreaks();
  // Kid-time analytics — page-level dwell logs to /api/analytics/dwell.
  // Skips /admin, keeps events short-lived (last 30 days server-side).
  usePageDwell(() => tier);
  // Seedlings & Explorers see a compact 2-subject preview inside the
  // "By Subject" nav group; the remaining 10 are hidden behind an
  // "📚 All 12 subjects" accordion. Pioneers always see the full list
  // (teens can handle the density, and they arrive with a subject in
  // mind). Kept as component state so opening the drawer once keeps the
  // expanded state until it's closed again.
  // Subjects accordion — applies to ALL tiers (was tier-gated to
  // seedlings/explorers). User asked (Feb 2026) that every menu shows
  // a preview of 4 core subjects + "See all 12" toggle so no tier's
  // drawer is overwhelming.
  const collapseSubjects = true;
  const [subjectsExpanded, setSubjectsExpanded] = useState(false);
  const SUBJECT_PREVIEW = [
    // Cycle 62 · Feb 2026 · Maths Academy pinned as the FIRST default
    // subject so kids see the flagship 83-lesson library without
    // tapping "Show all". Then the four core subjects.
    "maths-academy",
    "topic-geography", "topic-maths", "topic-english", "topic-science",
  ];
  // Seedlings gating — hide quiz + campaign nav for the seedlings tier.
  // Product principle: no unprompted testing, no gamified comparison for
  // the 4-6 age band. The routes still exist for direct URLs, but they
  // don't appear in menus. Streaks are elsewhere (Passport, Daily) and
  // gated via hasStreaks.
  const NAV_GATED_HIDE = new Set(
    !hasQuizzes ? ["quiz", "campaigns"] : [],
  );
  const [birthCode, setBirthCode] = useState(() => getBirthCountryCode());
  const location = useLocation();
  const navigate = useNavigate();
  // Active-learning mode: slim Back bar, compact footer, no tab bar / FAB / What's-next while inside an activity.
  const travelMode = useTravelMode() || location.pathname === "/travel" || location.pathname.startsWith("/travel/") || isAirlinePath(location.pathname) || airlineSessionActive(); // Travel Mode ON (or any /travel deep link) → slim travel chrome, no tab bar/footer
  const activityMode = isActiveLearningRoute(location.pathname) || travelMode;
  const isHome = location.pathname === "/";
  useLayoutEffect(() => { document.body.classList.toggle("activity-mode", activityMode); return () => document.body.classList.remove("activity-mode"); }, [activityMode]);
  // Child/adult boundary: an active lesson/practice/game is always a CHILD context. If the device was
  // left in Grown-up Mode, switch back to Kid Mode so exiting the activity never lands in parent chrome.
  useEffect(() => { if (activityMode && getMode() === "grownup") setMode("kid"); }, [activityMode]);

  // Feb 2026 — stamp every route visit so <WelcomeBack /> can greet
  // returning children (>12h gap) and remember which page they left.
  // stampVisit fires ONLY on beforeunload — never on mount — otherwise
  // we'd overwrite "last visit" every route change and the banner
  // would never see a >12h gap.
  React.useEffect(() => {
    import("@/lib/dataLightOffline").then(({ markVisit, stampVisit, flushMasteryQueue }) => {
      markVisit(location.pathname);
      const onUnload = () => stampVisit();
      const onOnline = () => flushMasteryQueue();
      window.addEventListener("beforeunload", onUnload);
      window.addEventListener("online", onOnline);
      return () => {
        window.removeEventListener("beforeunload", onUnload);
        window.removeEventListener("online", onOnline);
      };
    });
  }, [location.pathname]);

  // ── Grown-ups PIN gate state ─────────────────────────────────────
  // The "For Grown-ups" section is guarded so a pre-reader can't
  // wander into parent / teacher / admin surfaces. Two modes exist:
  //   • math  — a 7×8-style multiplication challenge (parents unlock
  //             in one tap; a 5-yr-old cannot).
  //   • pin   — a fixed 6-digit product-owner PIN, used for the
  //             lesson-admin + question-bank-admin routes which are
  //             sensitive enough to warrant a real password.
  // Route → required mode mapping. Per user request, ALL grown-up menu
  // items are unlocked EXCEPT the admin hub + its founder tools, which
  // stay behind the fixed product-owner PIN. Any key NOT listed here
  // navigates freely (no gate, no lock icon).
  const GATE_MODE_BY_KEY = {
    "admin-hub":              "pin",
    "admin-lessons":          "pin",
    "admin-questions":        "pin",
    "admin-north-star":       "pin",
    "admin-approvals":        "pin",
    "admin-outreach":         "pin",
    "admin-pilot-ops":        "pin",
    "admin-fundraise":        "pin",
    "admin-academy-coverage": "pin",
    "admin-outcomes":         "pin",
    "admin-metrics-defs":     "pin",
  };
  const [gateOpen, setGateOpen] = useState(false);
  const [gateMode, setGateMode] = useState("math");
  const [gateTarget, setGateTarget] = useState(null); // "/parents", etc.

  /**
   * Intercept a click on a grown-up nav item. If the required gate has
   * already been passed this session, we let the NavLink navigate as
   * normal. Otherwise we swallow the click, open the gate, and remember
   * the target so we can navigate on success.
   */
  const handleGrownupNavClick = (key, to, e) => {
    const mode = GATE_MODE_BY_KEY[key];
    if (!mode) return;                     // unlocked — navigate freely
    if (isGrownupGatePassed(mode)) return; // already unlocked this session
    e.preventDefault();
    e.stopPropagation();
    setGateMode(mode);
    setGateTarget(to);
    setGateOpen(true);
  };

  const onGateSuccess = () => {
    setGateOpen(false);
    if (gateTarget) {
      const t = gateTarget;
      setGateTarget(null);
      setMobileMore(false); // close mobile drawer if we opened from there
      navigate(t);
    }
  };
  const onGateCancel = () => {
    setGateOpen(false);
    setGateTarget(null);
  };

  // Top-1 age-tuned learning objective — single line at the top of both
  // the desktop More dropdown and the mobile drawer. Previously showed 3
  // items with taglines; that dominated the menu. One tight line lets the
  // rest of the nav breathe.
  const keyObjectives = keyObjectivesForAge(ageMeta?.defaultAge ?? null).slice(0, 1);

  // Keep the header's home-country chip in sync when the kid (re-)submits
  // the "Born in" picker.
  useEffect(() => {
    const onApplied = () => setBirthCode(getBirthCountryCode());
    window.addEventListener("we-personalize-applied", onApplied);
    return () => window.removeEventListener("we-personalize-applied", onApplied);
  }, []);

  const birthCountry = birthCode ? COUNTRIES.find((c) => c.code === birthCode) : null;

  // Sync the current age group to a `data-age` attribute on <html> so the
  // global CSS in App.css can subtly retune the UI per age:
  //  - little (5-7): larger base type, bolder borders
  //  - big   (8-10): default
  //  - pro   (11+):  denser type, slimmer chrome
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-age", ageMeta?.id || "big");
  }, [ageMeta?.id]);

  const humanize = (k) => k.charAt(0).toUpperCase() + k.slice(1);
  // Explicit friendly labels for keys whose humanised form would look ugly
  // ("Learning-plan" → "Learning Plan"). Overrides the i18n fallback path.
  const NAV_LABELS = {
    // Feb 2026 · Cycle 63 · Part 10 · canonical 5-item primary nav labels.
    // Order + wording follow the "Menu & Navigation Improvement" command:
    // TODAY (home/continue/missions), PLAY (games), LEARN (subjects),
    // EXPLORE (globe/countries), ME (passport/stamps/certificates).
    "home":          "Today",
    "learn-topics":  "Learn",
    "globe":         "Explore",
    "games":         "Play",
    "passport":      "Passport",
    "learn-pathways": "Learn",
    "play-create":   "Play",
    "my-quest":      "My Quest",
    "grown-ups":     "Grown-up Mode",
    "plan":          "Plan",
    // Phase-A primary nav labels (Cycle 60 · Feb 2026)
    "today":         "Today",
    "learn":         "Learn",
    "play":          "Play",
    "explore":       "Explore",
    "rewards":       "My Progress",
    "learning-plan": "Learning Plan",
    "term-report":   "Term Report",
    "schedule":      "Weekly & Annual Plan",
    "teacher":       "Teacher Dashboard",
    "teacher-console":    "Teacher Console · BETA",
    "homeschool-planner": "Homeschool Planner · BETA",
    "tutor-console":      "Tutor Console · BETA",
    "my-learning":   "My Learning",
    "real-teachers": "Live teaching (coming soon)",
    "knowledge":     "Themed Adventures",
    "admin-lessons": "Lesson Admin",
    "admin-questions": "Question Bank Admin",
    "admin-hub":          "Admin hub · all founder tools",
    "tools-hub":          "Teach & Explain · manipulatives + Daily 10",
    "my-quests":          "MyQuests · Live Quest host",
    "schools":       "Schools Admin",
    "for-schools":   "For Schools",
    "curriculum":    "Curriculum",
    "spelling-bee":  "Spelling Bee",
    "treasure-quest":"Treasure Quest",
    "tic-tac-toe":   "Tic Tac Toe",
    "games-hub":     "See all games →",
    "mandala":       "Mandala Studio",
    "stained-glass": "Stained Glass",
    "kaleidoscope":  "Kaleidoscope",
    "name-place-animal": "Name Place Animal",
    "languages":     "Languages",
    "advanced":      "Advanced Challenges",
    "ask":           "Ask the Globe ✨",
    "countries":     "Around the World",
    "hub-learn":     "Explore",
    "hub-create":    "Create",
    "hub-play":      "Play",
    "hub-discover":  "Adventure",
    "maths-academy": "Maths Academy",
    "dream-trip":    "Dream Trip",
    "geo-lines":     "Latitude & Longitude",
    "play-home":     "Play & Learn",
    "quest-games":   "Quest Games",
    animals:         "Animal Kingdom",
    "coloring":      "Colouring",
    "pixel-art":     "Pixel Art",
    "flag-artist":   "Flag Artist",
    "printables":    "Printable Packs",
    // Per-subject shortcuts — short, kid-friendly labels
    "topic-geography": "Geography",
    "topic-maths":     "Maths",
    "topic-science":   "Science",
    "topic-english":   "English",
    "topic-history":   "History",
    "topic-languages": "Languages",
    "topic-art":       "Art & Design",
    "topic-music":     "Music",
    "topic-ict":       "Computing",
    "topic-pe":        "PE",
    "topic-re":        "RE",
    "topic-pshe":      "PSHE",
    "reading":         "Phonics & Reading",
    "leveled-reading": "Reading & Letters",
    "language-quest":  "Language Quest",
    "stories":         "Stories",
    "times-tables-quest": "Times Tables Quest",
    "english-quest":   "English Quest",
    "letters":         "Letters from Explorers",
    "draw-along":      "Draw-Along",
    "handwriting":     "Handwriting",
    "money-time":      "Money & Time",
    "life-skills":     "Life Skills",
    "typing":          "Typing (Key Quest)",
    "code-quest":      "Code Quest",
    "sign-language":   "Sign Language (ASL)",
    "academy":         "Quest Academy",
    "live-quest":      "Live Quest",
    "code-breaker":    "Code Breaker",
    "everything":      "Everything",
    // Cycle 22B/C · 2-player duels
    "arcade":          "🕹️ Games Arcade",
    "duel-quiz":       "Quiz Duel",
    "duel-tables":     "Times-Table Rumble",
    "duel-spell":      "Spell-Off",
    "duel-continent":  "Continent Battle",
    "duel-reaction":   "Reaction Tap",
  };
  const buildItem = (k) => ({
    to: NAV_PATHS[k],
    label: t(`ui.nav.${k}`, NAV_LABELS[k] || humanize(k)),
    emoji: NAV_EMOJI[k],
    key: k,
  });
  const appMode = useAppMode();
  const a11y = useAttentionSettings();
  // Grown-up Mode: role-aware adult navigation (lib/roleRegistry) — parent/teacher/homeschool/tutor/school
  // see only their own tools; device tools (GROWNUP_NAV) when no role is inferred.
  const grownRole = appMode === "grownup" ? roleById(roleForPath(location.pathname)) : null;
  const PRIMARY = appMode === "grownup"
    ? (grownRole ? grownRole.nav.slice(0, 5).map((n) => ({ key: `role-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`, label: n.label, to: n.to, emoji: "" })) : GROWNUP_NAV)
    : PRIMARY_KEYS.map(buildItem);
  const SECONDARY = SECONDARY_KEYS.map(buildItem);
  const ALL = [...PRIMARY, ...SECONDARY];

  // Refresh progress on route change; auto-close the "more" sheet
  useEffect(() => {
    setProgress(loadProgress());
    setMobileMore(false);
  }, [location.pathname]);

  // Subscribe to progress changes from any page
  useEffect(() => {
    const handler = () => setProgress(loadProgress());
    window.addEventListener("we-progress-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("we-progress-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Brand-logo celebration: spin the globe icon when a new country is learned.
  useEffect(() => {
    const onLearn = () => {
      setBrandSpin(false);
      // Force reflow so the animation restarts even on rapid back-to-back learns
      requestAnimationFrame(() => requestAnimationFrame(() => setBrandSpin(true)));
      window.setTimeout(() => setBrandSpin(false), 1200);
    };
    window.addEventListener("we-country-learned", onLearn);
    return () => window.removeEventListener("we-country-learned", onLearn);
  }, []);

  // Install global tap-to-hear (Read Mode) exactly once. The listener is a
  // no-op unless the user (or parent) has flipped Read Mode on — it stays
  // out of the way for readers and unlocks pre-readers when needed.
  useEffect(() => {
    installTapToHear();
  }, []);

  // Apply accessibility settings to body
  useEffect(() => {
    document.body.classList.toggle("big-text", !!progress.settings.bigText);
    document.body.classList.toggle("high-contrast", !!progress.settings.highContrast);
  }, [progress.settings.bigText, progress.settings.highContrast]);

  const toggle = (key) => {
    const next = updateSettings({ [key]: !progress.settings[key] });
    setProgress(next);
  };

  // Is any "secondary" route active? (used to highlight the More menu)
  const secondaryActive = SECONDARY.some((s) =>
    s.to === "/" ? location.pathname === "/" : location.pathname.startsWith(s.to),
  );

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-night-950 text-slate-900 dark:text-starlight-50 transition-colors duration-smooth">
      {/* Cycle 14 (Feb 2026) — canvas migrated from `sky-50` to
          `paper-50` as part of the Paper Atlas design language.
          The warm parchment tone underpins every screen and lets
          hero cards feel like pages tucked into a notebook.
          Cycle 16 · Luminous World night-mode variant added — the
          `dark:bg-night-950` layers over the paper canvas when the
          user toggles Night. Text colour swaps to starlight-50 so
          all ink stays readable. */}
      {/* Top Nav — tap targets sized for kids ≥44px on mobile per iOS HIG */}
      {travelMode ? <TravelTopBar /> : activityMode ? <ActivityTopBar /> : (
      <header className="sticky top-0 z-40 border-b-4 border-slate-900 bg-paper-100 dark:bg-night-900">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 md:h-20 flex items-center gap-2.5 sm:gap-3">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="brand-link">
            {/* Full brand lockup — the image already contains the "MY
                WORLD QUEST" wordmark, globe, and plane. Previously it was
                nested inside a narrow yellow pill with overflow-hidden,
                which cropped the wordmark to something unreadable. We now
                render the PNG directly at a larger size so the identity
                is instantly recognizable on every viewport. */}
            {/* Feb 2026 · logo polish — the source PNG has a white
                "sticker" outline baked in, which looked like a boxy
                frame against the parchment header. `mix-blend-multiply`
                lets the parchment show through white pixels so the
                mark sits flush; slight size bump + no drop-shadow
                completes the polish. */}
            <img
              src="/brand-mark.webp"
              alt={t("ui.brand")}
              data-testid="brand-logo"
              width={640}
              height={420}
              className={cn(
                "h-12 sm:h-14 md:h-[72px] w-auto object-contain group-hover:-rotate-[2deg] transition-transform mix-blend-multiply dark:mix-blend-normal",
                brandSpin && "globe-spin-celebrate",
              )}
              loading="eager"
              decoding="async"
            />
            {/* Tagline hidden — the wordmark alone communicates the brand.
                Keep the DOM node so we can re-enable it easily if the
                header ever grows. */}
            <span className="sr-only">
              {t("ui.brand_subtitle")}
            </span>
          </Link>

          {/* Desktop primary nav — 4 canonical items visible from lg+.
              Feb 2026 · Cycle 62 — text labels ALWAYS render alongside
              icons (no icon-only at xl); consolidated to Home / Learn /
              Globe / Games so the label set matches the bottom tab bar
              at every breakpoint. */}
          <nav
            className="hidden md:flex items-center gap-0.5 lg:gap-1 ml-1 lg:ml-4 flex-shrink-0"
            aria-label="Primary"
            data-testid="primary-nav"
          >
            {PRIMARY.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${HUB_TESTID[n.key] || n.key}`}
                onClick={() => track("nav_destination_selected", { hub: HUB_TESTID[n.key] || n.key, via: "header" })}
                aria-label={n.label}
                className={({ isActive }) =>
                  cn(
                    "inline-flex items-center gap-1.5 px-2.5 lg:px-3 py-2 min-h-[44px] rounded-full font-bold text-[13px] lg:text-sm border-[3px] transition-all whitespace-nowrap shrink-0",
                    isActive
                      ? "bg-slate-900 text-white border-slate-900 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                      : "bg-transparent text-slate-900 border-transparent hover:bg-yellow-100 hover:border-slate-900",
                  )
                }
              >
                {n.emoji && <span className="hidden lg:inline text-base leading-none" aria-hidden>{n.emoji}</span>}
                {/* Always render the label — no icon-only breakpoint. */}
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>


          {/* Right cluster — Feb 2026 · Cycle 62 refactor.
              Reduced from 10+ competing chips to exactly THREE slots:
                1. Progress pill (⭐/🔥 compact — popover shows all 5 stats)
                2. Child / avatar switcher
                3. Overflow "⋯ More" menu (age, sound, Parents & Teachers,
                   accessibility toggles, subjects, admin — all inside)
              No individual stat chips, no age chip, no standalone
              Parents & Teachers button, no share/sound icons on the bar.
              Prevents the overlap that used to trigger below ~1440 px. */}
          <div
            className="ml-auto flex items-center justify-end gap-1.5 sm:gap-2 min-w-0"
            data-testid="header-right-cluster"
          >
            {/* Stars/streak counter + XP pill + Explorer rank are STATUS
                display, not navigation. Below lg they collapse into the
                More drawer (see MobileDrawer stats strip) so the header
                bar never overflows between 768–1023px. */}
            {/* Ranks/streaks are private & opt-in (My Quest toggle) — hidden by default in Kid Mode. */}
            {appMode !== "grownup" && (
            <div className="hidden xl:flex items-center gap-1.5 sm:gap-2" data-testid="header-status-cluster">
              <ProgressPill />
              <ExplorerRankBadge />
            </div>
            )}
            {appMode === "grownup" ? (
              <>
                <ChildSwitcher />
                <button type="button" onClick={() => { setMode("kid"); navigate("/"); }} data-testid="header-back-kid"
                  className="hidden sm:inline-flex shrink-0 items-center h-10 px-3 rounded-full border-[3px] border-slate-900 bg-emerald-200 font-bold text-xs whitespace-nowrap">← Kid Mode</button>
              </>
            ) : (
              <>
              <Link to="/travel" data-testid="header-travel-mode" aria-label="Travel Mode"
                className="hidden lg:inline-flex items-center gap-1 h-10 px-3 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-xs whitespace-nowrap shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px]">✈️ Travel</Link>
              <Link to="/grown-ups" onClick={() => setMode("grownup")} data-testid="header-grownups" aria-label="Grown-up Mode"
                className="hidden sm:inline-flex items-center h-10 px-3 rounded-full border-2 border-slate-400 text-slate-600 font-bold text-xs whitespace-nowrap hover:border-slate-900 hover:text-slate-900">Grown-ups</Link>
              </>
            )}

            {/* Feature discovery search — persistent on EVERY page, all
                sizes (Findability §2). */}
            <div className="hidden lg:block"><FeatureSearch /></div>

            {/* Desktop overflow menu — visible from lg+ (IA P1.5). */}
            <div className="hidden lg:flex">
              <DesktopDropdown
                secondaryActive={secondaryActive}
                keyObjectives={keyObjectives}
                navGroups={NAV_GROUPS}
                navGatedHide={NAV_GATED_HIDE}
                buildItem={buildItem}
                gateModeByKey={GATE_MODE_BY_KEY}
                handleGrownupNavClick={handleGrownupNavClick}
                collapseSubjects={collapseSubjects}
                subjectsExpanded={subjectsExpanded}
                setSubjectsExpanded={setSubjectsExpanded}
                subjectPreview={SUBJECT_PREVIEW}
                progress={progress}
                toggle={toggle}
                ageMeta={ageMeta}
                openGate={(target) => {
                  // All grown-up areas are unlocked (except admin) — go straight there.
                  setMobileMore(false);
                  navigate(target);
                }}
              />
            </div>

            {/* Mobile/Tablet overflow menu — opens the same drawer
                that lives inside <header> as <MobileDrawer />. */}
            <button
              type="button"
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMore((v) => !v)}
              className="lg:hidden shrink-0 inline-flex items-center gap-1.5 h-10 px-3 rounded-full border-[3px] border-slate-900 bg-yellow-300 shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] active:translate-y-[2px] transition-all font-bold text-sm"
              aria-label={mobileMore ? "Close menu" : "Open menu"}
              aria-expanded={mobileMore}
              title="More"
            >
              {mobileMore ? <X size={18} strokeWidth={3.5} /> : <MoreHorizontal size={18} strokeWidth={3.5} />}
              <span>More</span>
            </button>
          </div>
        </div>

        {/* Mobile/Tablet drawer — opens from header burger. Extracted to
            <MobileDrawer /> in Cycle 13.6 so Layout stays lean. */}
        <MobileDrawer open={mobileMore} setOpen={setMobileMore} />
      </header>
      )}

      <OfflineIndicator />
      <ContinentCertWatcher />

      {/* IA Phase 1 · P1.2 — the global Back/Home strip is deleted at every
          width (reversible: set localStorage `ff_global_backhome`="1").
          Browser back + the persistent Today tab cover escape. */}
      {(() => { try { return localStorage.getItem("ff_global_backhome") === "1"; } catch { return false; } })() && <GlobalBackHome />}

      {/* Feb 2026: global streak strip. Persistent across every page,
          so kids see their Foxy companion, streak, and weekly ring
          the moment they land. Pages should NOT render another copy
          of <DailyReviewCard /> in their body. */}
      {/* Feb 2026: global compact streak strip removed per user
          request — the "Foxy + day streak" strip that used to render
          on every page is now hidden. Streak data still tracks
          silently and shows on `/passport`. */}

      <IndexabilityMeta />
      <main className={activityMode ? "pb-4" : "pb-[calc(env(safe-area-inset-bottom)+72px)] md:pb-0"} data-activity-mode={activityMode ? "1" : "0"}>
        {/* Cycle 60 · Phase D · Parent Console 7-item nav. Renders only
            on `/parents/*`, `/safety`, `/achievements`, `/family` and
            `/my-learning` — every other page is untouched. */}
        <ParentConsoleNavSlot />
        <RememberVisit />
        <RouteErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </RouteErrorBoundary>
        {/* Shared "What's next?" — every child page ends with one obvious adventure (Kid Mode only). */}
        {appMode === "kid" && !activityMode && showsNextAdventure(location.pathname) && <NextAdventure />}
      </main>

      {/* Cycle 18 · Study Sky first-night delight. Global, one-shot. */}
      <StudySkyDelight />

      {/* Cycle 22 · Persistent Games shortcut. Hidden on the Arcade
          itself and on individual game canvases so it never blocks
          gameplay. */}
      {appMode === "kid" && !activityMode && !travelMode && !isActivityRoute(location.pathname) && <GamesQuickLinkFab />}
      <AccessibilityPanel />

      {!travelMode && (
      <footer className={`relative border-t-2 border-dashed border-slate-900 bg-white mt-2 ${activityMode ? "mb-0" : "mb-[calc(env(safe-area-inset-bottom)+68px)] md:mb-0"}`} data-testid="site-footer" data-footer-variant={activityMode ? "minimal" : isHome ? "full" : "compact"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 md:py-3 text-center">
          {/* Aug 2026 · Compact footer — kid views get essentials only
              (Parents+Safety+About+Contact); the fuller adult set (Blog,
              Certificates, Withdraw, Help, Lessons, Share, Philosophy)
              hides on <lg and is revealed on tablet/desktop where
              there's actually room. Mobile footprint dropped ~55%. */}
          <p
            data-testid="footer-tagline"
            className="text-[11px] md:text-xs text-slate-600 font-bold italic"
          >
            {t("home.subtitleShort", { defaultValue: "Explore the world — one lesson at a time." })}
          </p>
          {/* Cycle 61 · single short trust recap (only place on site
              besides the top-of-home Promise section). */}
          <p
            data-testid="footer-trust-recap"
            className="mt-1 text-[10px] md:text-xs font-black text-emerald-800"
          >
            {/* 9c · the English assessment POSTs answers + streams TTS, so it
                is NOT offline — drop that claim on that route only. */}
            {location.pathname.includes("/english-quest")
              ? "Free forever · No ads · No accounts."
              : "Free forever · No ads · No accounts · Offline-ready."}
          </p>
          {/* Site-wide Findability §1 · full crawlable six-column footer —
              real <Link> text, collapsible on mobile. Present on every page
              so any feature is reachable in ≤2 taps from anywhere. */}
          {!isActivityRoute(location.pathname) && !activityMode && (
            <div className="max-w-7xl mx-auto text-left mb-4" data-testid="footer-columns-wrap">
              <FooterColumns compact={!isHome} />
            </div>
          )}
          <nav
            className="mt-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] md:text-xs font-bold text-emerald-700"
            data-testid="footer-parent-links"
          >
            <FooterLink to="/sitemap" testid="footer-sitemap">Site Map</FooterLink>
            <FooterLink to="/everything" testid="footer-everything">Everything</FooterLink>
            <FooterLink to="/safety">Safety &amp; Privacy</FooterLink>
            <FooterLink to="/help" testid="footer-help">Help</FooterLink>
            <FooterLink to="/about" testid="footer-about">About</FooterLink>
            <FooterLink to="/contact" testid="footer-contact">Contact Us</FooterLink>
            <FooterLink to="/partners/travel-mode" testid="footer-partners">Travel Partners</FooterLink>
          </nav>
          <p className="mt-1 text-[10px] md:text-xs font-bold text-slate-500" data-testid="footer-credit">
            My World Quest · Made by ZEN <span aria-hidden>💛</span> for little explorers.
          </p>
        </div>
      </footer>
      )}

      {/* Language auto-detect prompt — surfaces once for AR/FR/HI
          visitors offering a one-tap UI switch to their native language.
          Sits above the mobile tabbar via z-[9999]. */}
      <LanguagePrompt />
      <InstallPrompt />

      {/* Feb 2026 · Cycle 62 · Bottom tab bar mirrors the desktop top
          nav EXACTLY — same 4 keys, same labels, same paths. Home ·
          Learn · Globe · Games. Passport is reachable from the
          Progress pill; everything else lives in the More menu. */}
      {!activityMode && <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t-4 border-slate-900 shadow-[0_-6px_18px_-6px_rgba(15,23,42,0.18)]"
        data-testid="mobile-tabbar"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 4px)" }}
        aria-label="Primary navigation"
      >
        <div className="grid grid-cols-5 max-w-3xl mx-auto">
          {PRIMARY.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.to === "/"}
              data-testid={`tab-${HUB_TESTID[n.key] || n.key}`}
              onClick={() => track("nav_destination_selected", { hub: HUB_TESTID[n.key] || n.key, via: "tabbar" })}
              aria-label={n.label}
              className={({ isActive }) => cn(
                "flex flex-col items-center justify-center gap-0.5 py-1.5 min-h-[56px] font-bold text-[11px] sm:text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 relative",
                isActive
                  ? "text-slate-900 bg-yellow-100 mwq-tab-active"
                  : "text-slate-700 hover:bg-slate-100 active:bg-slate-200",
              )}
            >
              <span aria-hidden className="mwq-tab-underline absolute top-0 left-1/2 -translate-x-1/2 h-1 w-8 rounded-b-full bg-slate-900 opacity-0" />
              {n.emoji ? <span aria-hidden className="text-2xl leading-none">{n.emoji}</span> : <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current mt-1" />}
              <span className="leading-tight text-[11px] sm:text-xs text-center px-1">
                {n.label}
              </span>
            </NavLink>
          ))}
        </div>
      </nav>}

      {/* Grown-up gate modal — opens when a kid taps a locked entry in
          the "For Grown-ups" section. Math mode for general parent
          pages; fixed PIN for admin routes. */}
      <GrownupPinGate
        open={gateOpen}
        mode={gateMode}
        onSuccess={onGateSuccess}
        onCancel={onGateCancel}
      />
    </div>
  );
}


// Recent Adventures — remembers concrete activities the child opens (no progress data).
function RememberVisit() {
  const { pathname, search } = useLocation();
  React.useEffect(() => { rememberVisit(pathname, search); }, [pathname, search]);
  return null;
}
